import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { ComponentType, HxCategoryService, ProductCategoryModel, ProductCategoryType, UiLabel } from 'hx-services';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NzModalService } from 'ng-zorro-antd/modal';
import { Subject } from 'rxjs';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NzAffixComponent } from 'ng-zorro-antd/affix';
import { HxBreadcrumbsComponent } from '@adm-web-app/components/breadcrumbs/breadcrumbs.component';
import { NzPageHeaderComponent, NzPageHeaderTitleDirective } from 'ng-zorro-antd/page-header';
import { NzSkeletonComponent } from 'ng-zorro-antd/skeleton';
import { NzFormControlComponent, NzFormDirective, NzFormItemComponent, NzFormLabelComponent } from 'ng-zorro-antd/form';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { HxBrandSelectComponent, HxEnumSelectComponent, HxLabelEditComponent } from 'hx-components';
import { NzRadioComponent, NzRadioGroupComponent } from 'ng-zorro-antd/radio';
import { NzInputNumberComponent } from 'ng-zorro-antd/input-number';
import { NgIf } from '@angular/common';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';

import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzTagComponent } from 'ng-zorro-antd/tag';

interface FormModel {
  enabled: boolean;
  title: UiLabel;
  brandId: number;
  type: ProductCategoryType;
  components: ComponentType[];
  seq: number;
}

@Component({
    selector: 'hc-product-category-edit',
    templateUrl: './product-category-edit.component.html',
    styleUrls: ['./product-category-edit.component.less'],
  imports: [NzAffixComponent, HxBreadcrumbsComponent, NzPageHeaderComponent, NzPageHeaderTitleDirective, TranslocoDirective, NzSkeletonComponent, FormsModule, NzFormDirective, ReactiveFormsModule, NzRowDirective, NzColDirective, NzFormItemComponent, NzFormLabelComponent, NzFormControlComponent, HxBrandSelectComponent, NzRadioGroupComponent, NzRadioComponent, HxLabelEditComponent, NzInputNumberComponent, HxEnumSelectComponent, NgIf, NzAlertComponent, NzButtonComponent, NzWaveDirective, NzIconDirective, NzTagComponent, TranslocoPipe]
})
export class ProductCategoryEditComponent implements OnInit, OnDestroy {
  model!: ProductCategoryModel;
  isEdit = false;
  isCreate = false;

  form!: UntypedFormGroup;
  isLoading = {
    model: false,
  };
  isSaving = false;
  ProductCategoryType = ProductCategoryType;
  ComponentType = ComponentType;
  private readonly path = 'product-categories';
  private $destroyed = new Subject<void>();

  constructor(
    protected aRoute: ActivatedRoute,
    protected fb: UntypedFormBuilder,
    protected categoryService: HxCategoryService,
    protected router: Router,
    protected modal: NzModalService,
    protected notification: NzNotificationService,
    protected tr: TranslocoService,
  ) {
  }

  ngOnInit(): void {
    const routeData: Data = this.aRoute.snapshot.data;
    this.model = routeData['model'] as ProductCategoryModel;
    if (this.model) {
      this.isEdit = true;
    } else {
      this.model = {} as ProductCategoryModel;
      this.isCreate = true;
    }
    this.form = this.fb.group({
      enabled: [true, [Validators.required]],
      title: [null, [Validators.required]],
      brandId: [null, [Validators.required]],
      type: [null],
      components: [null, [Validators.required]],
      seq: [null, [Validators.required]],
    });
    this.updateForm();
  }

  ngOnDestroy(): void {
    this.$destroyed.next();
    this.$destroyed.complete();
  }

  async submitForm() {
    Object.values(this.form.controls).forEach(ctrl => {
      ctrl.markAsDirty();
      ctrl.updateValueAndValidity();
    });
    if (this.form.valid) {
      const formModel = this.form.getRawValue() as FormModel;
      const model: Partial<ProductCategoryModel> = {
        id: this.model.id,
        enabled: formModel.enabled,
        title: formModel.title,
        brandId: formModel.brandId,
        type: formModel.type,
        components: formModel.components,
        seq: formModel.seq,
      };

      this.isSaving = true;
      try {
        const result = await this.categoryService.saveCategory(model as ProductCategoryModel);
        this.form.markAsPristine();
        this.notification.create(
          'success',
          this.tr.translate('nav.ProductCategories'),
          this.tr.translate('nt.dataSaved')
        );
        if (this.isCreate) {
          this.router.navigate([`/${this.path}/${result.id}/edit`]);
        } else {
          this.isLoading.model = true;
          this.model = await this.categoryService.getCategory(result.id);
          this.isLoading.model = false;
          this.updateForm();
        }
      } catch (err: any) {
        this.notification.error(this.tr.translate('nav.ProductCategories'), this.tr.translate(err?.error?.message));
      } finally {
        this.isSaving = false;
      }
    } else {
      console.log('not valid form', this.form.errors);
    }
  }

  private updateForm() {
    const formModel: Partial<FormModel> = {
      enabled: this.model.enabled,
      title: this.model.title,
      brandId: this.model.brandId,
      type: this.model.type,
      components: this.model.components,
      seq: this.model.seq ?? null,

    };
    this.form.patchValue(formModel);
  }
}
